import { HashLink } from "@/components/HashLink";
import { MEGA_MENU_EXTERNAL_URLS } from "@/i18n/menu-external-urls";
import { useLang, useT } from "@/lib/i18n";
import { products, solutions } from "@/lib/sections";
import React from "react";

type FooterItem = { label: string; href?: string };
type FooterCol = { title: string; items: FooterItem[] };

interface FooterProps {
  /** Kept for backward compat with IndexPage; unused in V2 — links navigate directly via react-router. */
  setActiveTab?: (tab: string) => void;
}

const Footer: React.FC<FooterProps> = () => {
  const t = useT();
  const { lang } = useLang();
  const cols: FooterCol[] = [
    {
      title: t("Products", "产品"),
      items: products.map((p) => ({
        label: t(p.en, p.zh),
        href: p.slug === "orangeo"
          ? MEGA_MENU_EXTERNAL_URLS["menu.prod.orangeo"]
          : `/products#${p.slug}`,
      })),
    },
    ...solutions.map((g) => ({
      title: t(g.headerEn, g.headerZh),
      items: g.items.map((i) => ({ label: t(i.en, i.zh), href: `/solutions#${i.slug}` })),
    })),
    {
      title: t("Platform", "平台"),
      items: [
        {
          label: t("Data Center", "数据中心"),
          href: MEGA_MENU_EXTERNAL_URLS["menu.plat.dataCenter"],
        },
        { label: t("Model Library", "模型库"), href: "https://models.photog.art" },
        {
          label: t("Skills", "技能"),
          href: MEGA_MENU_EXTERNAL_URLS["menu.plat.skills"],
        },
        { label: t("API Docs", "API 文档") },
      ],
    },
    {
      title: t("Inspiration", "灵感"),
      items: [
        { label: t("Video", "视频"), href: "/library?tab=video" },
        { label: t("Voice", "声音"), href: "/library?tab=voice" },
        { label: t("Model", "模特"), href: "/library?tab=model" },
      ],
    },
    {
      title: t("Company", "公司"),
      items: [
        { label: t("About", "关于"), href: "/company#about" },
        { label: t("News", "新闻") },
        { label: t("Contact", "联系"), href: "/company#contact" },
      ],
    },
    {
      title: t("Legal", "法律"),
      items: [
        { label: t("Terms", "条款"), href: `https://policies.photog.art/terms?lang=${lang}` },
        { label: t("Privacy", "隐私"), href: `https://policies.photog.art/privacy?lang=${lang}` },
        { label: "DPA" },
        { label: t("Security", "安全") },
      ],
    },
  ];

  return (
    <div className="landing-v2">
      <footer className="relative   border-t border-border">
        <div className="mx-auto max-w-[min(1760px,95vw)] px-6 py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-10 text-sm">
            {cols.map((c) => (
              <div key={c.title}>
                <h4 className="text-foreground font-medium mb-4">{c.title}</h4>
                <ul className="space-y-2 text-muted-foreground">
                  {c.items.map((i) => {
                    const isExternal = i.href && /^https?:\/\//.test(i.href);
                    return (
                      <li key={i.label}>
                        {i.href ? (
                          isExternal ? (
                            <a
                              href={i.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:text-foreground transition-colors"
                            >
                              {i.label}
                            </a>
                          ) : (
                            <HashLink to={i.href} className="hover:text-foreground transition-colors">
                              {i.label}
                            </HashLink>
                          )
                        ) : (
                          <a href="#" className="hover:text-foreground transition-colors">
                            {i.label}
                          </a>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
          <div className="divider-nebula my-10" />
          <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-4 text-xs text-muted-foreground">
            <div className="hidden md:block" />
            <p className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 opacity-70">
              <a
                href="https://beian.miit.gov.cn/"
                target="_blank"
                rel="noreferrer"
                className="hover:text-foreground transition-colors"
              >
                粤ICP备 2024322593号
              </a>
              <span className="opacity-50">|</span>
              <span>Copyright © 2025 OranAI. {t("All rights reserved.", "版权所有")}</span>
            </p>
            <p className="flex flex-wrap items-center md:justify-end gap-x-2 gap-y-1">
              <span>
                {t("Contact us:", "联系我们：")}{" "}
                <a href="mailto:support@oran.cn" className="hover:text-foreground transition-colors">
                  support@oran.cn
                </a>
              </span>
              {/* <span className="opacity-50">|</span>
              <span>
                {t("Phone:", "电话：")}{" "}
                <a href="tel:+8619946254041" className="hover:text-foreground transition-colors">
                  +86 19946254041
                </a>
              </span> */}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Footer;
